import {
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Search } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { RoiFilter, InterpretedTypeGroup } from './types';

type StructureFilterPanelProps = {
    filters: RoiFilter;
    presentTypeGroups: InterpretedTypeGroup[];
    hasActiveFilters: boolean;
    allFilteredSelected: boolean;
    someFilteredSelected: boolean;
    conflictingRoiNumbers: Set<number>;
    overwriteExisting: boolean;
    selectedCount: number;
    onToggleFilter: (key: 'visibleOnly' | 'approvedOnly') => void;
    onToggleTypeGroup: (types: readonly RoiInterpretedType[]) => void;
    onIsGroupActive: (types: readonly RoiInterpretedType[]) => boolean;
    onClearFilters: () => void;
    onUpdateSearchText: (searchText: string) => void;
    onToggleAllFiltered: () => void;
    onToggleOverwrite: () => void;
};

export default function StructureFilterPanel({
    filters,
    presentTypeGroups,
    hasActiveFilters,
    allFilteredSelected,
    someFilteredSelected,
    conflictingRoiNumbers,
    overwriteExisting,
    selectedCount,
    onToggleFilter,
    onToggleTypeGroup,
    onIsGroupActive,
    onClearFilters,
    onUpdateSearchText,
    onToggleAllFiltered,
    onToggleOverwrite,
}: StructureFilterPanelProps) {
    const intl = useIntl();

    return (
        <Box>
            <Box
                px={2}
                py={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography
                    variant="subtitle2"
                    fontSize={12}
                    color="text.secondary"
                >
                    <FormattedMessage
                        id="structureSet.copy.roiPreview"
                        defaultMessage="Structures to copy ({count})"
                        values={{ count: selectedCount }}
                    />
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={allFilteredSelected}
                            indeterminate={
                                someFilteredSelected && !allFilteredSelected
                            }
                            onChange={onToggleAllFiltered}
                        />
                    }
                    label={
                        <Typography fontSize={11}>
                            <FormattedMessage
                                id="structureSet.copy.selectAll"
                                defaultMessage="Select all"
                            />
                        </Typography>
                    }
                    sx={{ mr: 0 }}
                />
            </Box>

            <Box px={2} pb={1}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder={intl.formatMessage({
                        id: 'structureSet.copy.searchRoi',
                        defaultMessage: 'Search structures...',
                    })}
                    value={filters.searchText}
                    onChange={(e) => onUpdateSearchText(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <Search
                                size={13}
                                style={{
                                    marginRight: 6,
                                    opacity: 0.4,
                                    flexShrink: 0,
                                }}
                            />
                        ),
                    }}
                    data-testid="roi-search-input"
                />
                <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.5}
                    mt={1}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={filters.visibleOnly}
                                onChange={() => onToggleFilter('visibleOnly')}
                                sx={{ p: 0.25, ml: 0.5 }}
                            />
                        }
                        label={
                            <Typography fontSize={11}>
                                <FormattedMessage
                                    id="structureSet.filter.visibleOnly"
                                    defaultMessage="Visible"
                                />
                            </Typography>
                        }
                        sx={{ mr: 1.5 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={filters.approvedOnly}
                                onChange={() => onToggleFilter('approvedOnly')}
                                sx={{ p: 0.25 }}
                            />
                        }
                        label={
                            <Typography fontSize={11}>
                                <FormattedMessage
                                    id="structureSet.filter.approvedOnly"
                                    defaultMessage="Approved"
                                />
                            </Typography>
                        }
                    />
                    {presentTypeGroups.map((group) => (
                        <Chip
                            key={group.labelId}
                            label={intl.formatMessage({
                                id: group.labelId,
                                defaultMessage: group.defaultLabel,
                            })}
                            size="small"
                            variant={
                                onIsGroupActive(group.types)
                                    ? 'filled'
                                    : 'outlined'
                            }
                            color={
                                onIsGroupActive(group.types)
                                    ? 'primary'
                                    : 'default'
                            }
                            onClick={() => onToggleTypeGroup(group.types)}
                            sx={{ fontSize: 10, height: 22 }}
                        />
                    ))}
                    {hasActiveFilters && (
                        <Chip
                            label={intl.formatMessage({
                                id: 'structureSet.filter.clearAll',
                                defaultMessage: 'Clear',
                            })}
                            size="small"
                            variant="outlined"
                            onDelete={onClearFilters}
                            sx={{ fontSize: 10, height: 22 }}
                        />
                    )}
                </Stack>
            </Box>

            {conflictingRoiNumbers.size > 0 && (
                <Box pb={1} pl={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={overwriteExisting}
                                onChange={onToggleOverwrite}
                                sx={{ p: 0.25, ml: 0.5 }}
                            />
                        }
                        label={
                            <Typography fontSize={11}>
                                <FormattedMessage
                                    id="structureSet.copy.overwriteExisting"
                                    defaultMessage="Overwrite structures with same name"
                                />
                            </Typography>
                        }
                    />
                </Box>
            )}
        </Box>
    );
}
